import { useEffect, useRef, type FormEvent } from "react";
import { createPortal } from "react-dom";
import { splitInlineStrong } from "./preparation-inline-strong.js";
import "./preparation-sandbox-modal.css";

type Message = { actor: "customer" | "ai"; text: string };

export function PreparationSandboxModal({ companyName, assistantName, messages, pending, pendingText, error, text, onTextChange, onSend, onReset, onClose }: {
  companyName: string;
  assistantName: string;
  messages: Message[];
  pending: boolean;
  pendingText: string;
  error: string;
  text: string;
  onTextChange: (value: string) => void;
  onSend: () => void;
  onReset: () => void;
  onClose: () => void;
}) {
  const dialog = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const history = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    input.current?.focus();
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") { event.preventDefault(); close.current(); return; }
      if (event.key !== "Tab" || !dialog.current) return;
      const focusable = [...dialog.current.querySelectorAll<HTMLElement>("button:not(:disabled), input:not(:disabled)")];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => { document.removeEventListener("keydown", onKeyDown); document.body.style.overflow = previousOverflow; previousFocus?.focus(); };
  }, []);

  useEffect(() => { history.current?.scrollTo({ top: history.current.scrollHeight, behavior: "smooth" }); }, [messages.length, pending, error]);

  function submit(event: FormEvent) { event.preventDefault(); onSend(); }
  const name = assistantName.trim() || "Assistente G3.AI";
  return createPortal(
    <div className="prep-chat-backdrop" onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="prep-chat-dialog" role="dialog" aria-modal="true" aria-labelledby="prep-chat-title" aria-describedby="prep-chat-description" ref={dialog}>
        <header className="prep-chat-header">
          <button type="button" className="prep-chat-close" aria-label="Fechar conversa de teste" onClick={onClose}>←</button>
          <div className="prep-chat-avatar" aria-hidden="true">✦</div>
          <div className="prep-chat-identity"><h2 id="prep-chat-title">{name}</h2><p>{companyName.trim() || "Sua empresa"} · Simulação</p></div>
          <button type="button" className="prep-chat-reset" onClick={onReset} disabled={pending} aria-label="Reiniciar conversa de teste">↻ <span>Reiniciar</span></button>
        </header>
        <div className="prep-chat-disclosure" id="prep-chat-description"><span className="prep-chat-status-dot" aria-hidden="true"/>SANDBOX — Ambiente de teste <span>· Usa somente o rascunho desta empresa</span></div>
        <div className="prep-chat-history" ref={history} role="log" aria-live="polite" aria-relevant="additions text">
          {!messages.length && !pending && <div className="prep-chat-welcome"><span aria-hidden="true">✦</span><strong>Experimente sua IA antes de conectar o WhatsApp</strong><p>Faça uma pergunta como se fosse um cliente. As respostas usam as informações que você preparou.</p></div>}
          {messages.map((message, index) => <div className={`prep-chat-message ${message.actor}`} key={index}><span>{message.actor === "ai" ? splitInlineStrong(message.text).map((part, partIndex) => part.strong ? <strong key={partIndex}>{part.text}</strong> : part.text) : message.text}</span></div>)}
          {pendingText && <div className="prep-chat-message customer"><span>{pendingText}</span></div>}
          {pending && <div className="prep-chat-message ai prep-chat-thinking" role="status" aria-label="Preparando resposta"><span><i/><i/><i/></span></div>}
          {error && <p className="prep-chat-error" role="alert">{error}</p>}
        </div>
        {!messages.length && !pending && <div className="prep-chat-prompts" aria-label="Perguntas de exemplo">{["Quais serviços vocês oferecem?", "Como posso falar com a equipe?"].map(question => <button type="button" key={question} onClick={() => { onTextChange(question); input.current?.focus(); }}>{question}</button>)}</div>}
        <form className="prep-chat-composer" onSubmit={submit}><input ref={input} aria-label="Mensagem de teste" value={text} onChange={event => onTextChange(event.target.value)} placeholder="Escreva sua pergunta…" disabled={pending} autoComplete="off"/><button type="submit" disabled={pending || !text.trim()} aria-label="Enviar mensagem de teste">➤</button></form>
        <p className="prep-chat-footnote">Conversa simulada · Sem envio ao WhatsApp</p>
      </div>
    </div>, document.body
  );
}
